import { createContext, use, useCallback, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { AlertTriangle, CheckCircle2, Info, X, XCircle } from 'lucide-react';
import { cx } from '../cx';

export type ToastTone = 'info' | 'success' | 'warning' | 'error';

export interface Toast {
  id: string;
  tone: ToastTone;
  title: string;
  description?: string;
  /** Milliseconds before auto-dismiss; 0 keeps it until dismissed. Default 4000 below `sm`, 5000 above. */
  duration?: number;
}

/**
 * Tailwind's `sm` line, which is where the viewport switches from the phone's top strip to the desktop's
 * bottom-right stack — so the two rules that are not CSS (how long a toast stays, how many may pile up)
 * change at the same place, and admin's desktop behaviour is exactly what it was.
 */
const NARROW = '(max-width: 639.98px)';

function isNarrow(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia(NARROW).matches
    : false;
}

/** On a phone a third toast would cover the app bar; the oldest leaves instead. Desktop keeps stacking. */
export const MAX_VISIBLE_TOASTS_NARROW = 2;

/**
 * The stack, as a pure function: newest last, an existing id replaced in place rather than duplicated, and
 * never more than `max` on screen — the oldest is pushed off the top when a new one arrives.
 */
export function stackToasts(list: Toast[], next: Toast, max: number): Toast[] {
  const merged = [...list.filter((t) => t.id !== next.id), next];
  return merged.length > max ? merged.slice(merged.length - max) : merged;
}

export interface ToastContextValue {
  toasts: Toast[];
  show: (toast: Omit<Toast, 'id'> & { id?: string }) => string;
  success: (title: string, description?: string) => string;
  error: (title: string, description?: string) => string;
  info: (title: string, description?: string) => string;
  warning: (title: string, description?: string) => string;
  dismiss: (id: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export interface ToastProviderProps {
  children: ReactNode;
  /**
   * Called once for every toast that is shown, with its tone. The shell maps it to a haptic (success and
   * error have their own patterns on both platforms); on the web nothing listens. It lives here, as a
   * callback, so the kit never has to know that Capacitor exists.
   */
  onToast?: (tone: ToastTone) => void;
}

/** Wrap the app once (inside the router is fine): `<ToastProvider><App/></ToastProvider>`. */
export function ToastProvider({ children, onToast }: ToastProviderProps) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>());
  // Read through a ref so `show` keeps one identity for the life of the provider: it is in the context
  // value every consumer depends on.
  const onToastRef = useRef(onToast);
  onToastRef.current = onToast;

  const dismiss = useCallback((id: string) => {
    setToasts((list) => list.filter((t) => t.id !== id));
    const timer = timers.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timers.current.delete(id);
    }
  }, []);

  const show = useCallback<ToastContextValue['show']>(
    (toast) => {
      const id = toast.id ?? `t${Date.now()}${Math.random().toString(36).slice(2, 7)}`;
      const narrow = isNarrow();
      // Four seconds on a phone, where the strip sits over the content it is talking about; five on a
      // desktop, as today. A toast that was given an explicit duration keeps it.
      const duration = toast.duration ?? (narrow ? 4000 : 5000);
      const max = narrow ? MAX_VISIBLE_TOASTS_NARROW : Number.POSITIVE_INFINITY;
      setToasts((list) => stackToasts(list, { ...toast, id }, max));
      if (duration > 0)
        timers.current.set(
          id,
          setTimeout(() => dismiss(id), duration),
        );
      onToastRef.current?.(toast.tone);
      return id;
    },
    [dismiss],
  );

  const value = useMemo<ToastContextValue>(
    () => ({
      toasts,
      show,
      dismiss,
      success: (title, description) => show({ tone: 'success', title, description }),
      error: (title, description) => show({ tone: 'error', title, description, duration: 8000 }),
      info: (title, description) => show({ tone: 'info', title, description }),
      warning: (title, description) => show({ tone: 'warning', title, description }),
    }),
    [toasts, show, dismiss],
  );

  return (
    <ToastContext value={value}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={dismiss} />
    </ToastContext>
  );
}

/** `const toast = useToast(); toast.success('Saved');` */
export function useToast(): ToastContextValue {
  const ctx = use(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>');
  return ctx;
}

const icons = { info: Info, success: CheckCircle2, warning: AlertTriangle, error: XCircle };
const toneClass: Record<ToastTone, string> = {
  info: 'border-info/40 text-info',
  success: 'border-success/40 text-success',
  warning: 'border-warning/40 text-warning',
  error: 'border-error/40 text-error',
};

function ToastViewport({ toasts, onDismiss }: { toasts: Toast[]; onDismiss: (id: string) => void }) {
  if (toasts.length === 0) return null;
  return (
    <div
      aria-live="polite"
      className={cx(
        // Below sm the bottom of the screen belongs to the tab bar, the home indicator and sometimes a
        // keyboard, so a toast comes down from under the app bar instead. From sm up: today's stack in
        // the bottom-right corner, class for class.
        'pointer-events-none fixed inset-x-3 top-[calc(var(--sf-safe-top)+3.25rem)] z-[60] flex flex-col items-center gap-2',
        'sm:inset-x-auto sm:top-auto sm:right-4 sm:bottom-4 sm:items-end sm:p-3',
      )}
    >
      {toasts.map((t) => {
        const Icon = icons[t.tone];
        return (
          <div
            key={t.id}
            className={cx(
              'sf-toast-in pointer-events-auto flex w-full max-w-sm items-start gap-3 rounded-card border bg-surface p-3 shadow-pop',
              toneClass[t.tone],
            )}
          >
            <Icon className="mt-0.5 h-5 w-5 shrink-0" aria-hidden />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-fg">{t.title}</p>
              {t.description && <p className="mt-0.5 text-sm text-fg-muted">{t.description}</p>}
            </div>
            <button
              type="button"
              onClick={() => onDismiss(t.id)}
              aria-label="Dismiss"
              // 44px of target on a phone, today's 24px from sm up — where the press tint would be a
              // change to a rendering that is otherwise untouched.
              className="-m-3 rounded p-3 text-fg-subtle hover:text-fg max-sm:active:bg-surface-2 sm:-m-1 sm:p-1"
            >
              <X className="h-4 w-4" aria-hidden />
            </button>
          </div>
        );
      })}
    </div>
  );
}
